import { useModals } from "@/features/modals/hooks";
import { type UserInfo, type hfb, useSearchTeam, useTeam } from "@repo/api";
import {
  Badge,
  FormField,
  InlineMessage,
  ListView,
  ListViewItem,
  Loading,
  Option,
  Select,
  Switch,
  Text,
  briefcaseIcon,
  locationPinIcon,
  peopleIcon,
  tagIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  ElementsContainer,
  Line,
  SectionContainer,
  StatelessLabelField,
  translateHFB,
  useOrderedCustomerMeetingPoints,
} from "@repo/ui";
import { hasItems, trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";

import {
  type ManageTeamState,
  type ManageTeamStateActions,
} from "./ManageTeamReducer";
import { MemberList } from "./MemberList";

interface Props {
  state: ManageTeamState;
  actions: ManageTeamStateActions;
}

export const TeamDetailStep = ({ state, actions }: Props) => {
  const { data: team, isLoading } = useTeam(
    state.selectedTeamId,
    !state.editMode
  );

  const { data: businessUnits } = useOrderedCustomerMeetingPoints();
  const { pushModal } = useModals();
  const { data: searchResult } = useSearchTeam({
    hfbs: team?.hfbs,
    buCodes: team?.buCode ? [team?.buCode] : undefined,
  });

  const shouldSetDefaultTeam =
    isEmpty(searchResult?.filter((el) => el.teamId !== team?.teamId)) &&
    team?.default === false;

  if (isLoading) {
    return <Loading text={t("prompt.loading")} />;
  }

  if (team === undefined) {
    return (
      <Text>
        <T translationKey="prompt.no_team_details" />
      </Text>
    );
  }
  return (
    <SectionContainer>
      {state.editMode ? (
        <ElementsContainer>
          <FormField
            valid={Boolean(state.updatedTeam?.teamName)}
            shouldValidate
            validation={{
              msg: t("label.required_field"),
              id: "error",
              type: "error",
            }}
          >
            <StatelessLabelField
              id="team-name-field"
              label={t("label.name")}
              editable
              value={state.updatedTeam?.teamName}
              onChange={(newValue) => {
                if (state.updatedTeam) {
                  actions.setUpdatedTeam({
                    ...state.updatedTeam,
                    teamName: newValue ?? "",
                  });
                }
              }}
            />
          </FormField>

          <FormField
            shouldValidate
            valid={(state.updatedTeam?.description ?? "").length < 120}
            validation={{
              msg: t("prompt.max_120_characters"),
              id: "error",
              type: "error",
            }}
          >
            <StatelessLabelField
              id="team-name-field"
              label={t("label.description")}
              editable
              value={state.updatedTeam?.description}
              onChange={(newValue) => {
                if (state.updatedTeam) {
                  actions.setUpdatedTeam({
                    ...state.updatedTeam,
                    description: newValue ?? "",
                  });
                }
              }}
            />
          </FormField>

          <FormField
            shouldValidate
            valid={
              state.updatedTeam?.buCode !== undefined &&
              state.updatedTeam.buCode !== t("action.choose_an_option")
            }
            validation={{
              msg: t("label.required_field"),
              id: "error",
              type: "error",
            }}
          >
            <Select
              id="team-bu-select"
              label={t("label.business_unit")}
              defaultValue={team.buCode}
              onChange={(e) => {
                if (state.updatedTeam) {
                  actions.setUpdatedTeam({
                    ...state.updatedTeam,
                    buCode: e.target.value,
                  });
                }
                trackAnalytics("select_business_unit");
              }}
            >
              {businessUnits.map((el) => (
                <Option key={el[0]} value={el[0]} name={el[1]} />
              ))}
            </Select>
          </FormField>

          <ListView id="manage-team-department" showDivider={false}>
            <ListViewItem
              control="navigational"
              title={t("label.department")}
              description={
                hasItems(state.updatedTeam?.hfbs) ? (
                  state.updatedTeam?.hfbs
                    ?.map((el) => translateHFB(el))
                    .join(", ")
                ) : (
                  <FormField
                    valid={state.updatedTeam?.hfbs !== undefined}
                    shouldValidate
                    validation={{
                      msg: t("label.required_field"),
                      id: "error",
                      type: "error",
                    }}
                  />
                )
              }
              onClick={() => {
                pushModal({
                  type: "SELECT_DEPARTMENT",
                  selected: state.updatedTeam?.hfbs ?? ([] as hfb[]),
                  onUpdate: (newHfbs) => {
                    if (state.updatedTeam) {
                      actions.setUpdatedTeam({
                        ...state.updatedTeam,
                        hfbs: newHfbs,
                      });
                    }
                  },
                  useBokaConfig: true,
                });
                trackAnalytics("open_department_modal");
              }}
            />
          </ListView>

          {shouldSetDefaultTeam && (
            <InlineMessage
              title={t("prompt.set_default_team")}
              body={t("prompt.set_default_team_desc")}
              variant="cautionary"
            />
          )}
          <FormField
            fieldHelper={{
              msg: t("prompt.default_team_description"),
            }}
          >
            <Switch
              id="team-default"
              subtle
              label={t("label.default_team")}
              value={(team.default ?? false).toString()}
              defaultChecked={team.default}
              onChange={(e) => {
                if (state.updatedTeam) {
                  actions.setUpdatedTeam({
                    ...state.updatedTeam,
                    default: e.target.checked,
                  });
                }
              }}
            />
          </FormField>
        </ElementsContainer>
      ) : (
        <ElementsContainer>
          <Text textStyle="Heading.M">{team.teamName}</Text>
          {team.default && (
            // Needs empty div for layout purposes
            <div>
              <Badge
                colour="blue"
                label={t("label.default_team")}
                size="small"
              />
            </div>
          )}
          <ListView id="team-details__view" showDivider={false}>
            <ListViewItem
              leadingIcon={peopleIcon}
              title={t("label.description")}
              description={team.description}
            />
            <ListViewItem
              leadingIcon={locationPinIcon}
              title={t("label.business_unit")}
              description={
                businessUnits.find((el) => el[0] === team.buCode)?.[1]
              }
            />
            <ListViewItem
              leadingIcon={briefcaseIcon}
              title={t("label.department")}
              description={team.hfbs?.map((el) => translateHFB(el)).join(", ")}
            />
            <ListViewItem
              leadingIcon={tagIcon}
              title={t("label.team_id")}
              description={team.teamId?.toString()}
            />
          </ListView>
        </ElementsContainer>
      )}

      <Line />

      <ElementsContainer>
        <Text textStyle="Heading.S">{t("label.members")}</Text>
        <MemberList
          key={`team-detail-${team.teamId}-${state.editMode}`}
          members={team.teamMembers}
          setMembers={(members: UserInfo[]) => {
            if (state.updatedTeam) {
              actions.setUpdatedTeam({
                ...state.updatedTeam,
                teamMembers: members,
              });
            }
          }}
          editMode={state.editMode}
          managerEmailId={team.manager.emailId}
          setManager={(manager: UserInfo) => {
            if (state.updatedTeam) {
              actions.setUpdatedTeam({
                ...state.updatedTeam,
                manager,
              });
            }
          }}
        />
      </ElementsContainer>
    </SectionContainer>
  );
};
